/*---------------------------------------------------------------------------------------------------*/
/* Filename: request-body.service.ts                                                                 */
/* Description: This file is for form a request body for all the API and cloud function calls        */
/* Created: 24/01/2020                                                                               */
/* Copyright (c) 2023 Lottosocial by Hatchster                                                       */
/*------------------------------------------------ --------------------------------------------------*/

import { Injectable, Injector, OnDestroy, signal } from "@angular/core";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { getAuth } from "firebase/auth";
import { firstValueFrom, from, Observable, of, Subject, throwError } from "rxjs";
import { filter, map, switchMap, take, takeUntil } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { CommonVariable } from "../app.constants";
import { CommonService } from "./common/common.service";
import { SalesJourneyIdentityService } from "./sales-journey-identity.service";
@Injectable({
  providedIn: "root",
})
export class RequestBodyService implements OnDestroy {
  customerId: string
  preferredCurrency: string;
  public checkApiPostReqProfileImg;
  public checkApiPostReqRemovePhoto;
  private destroy$ = new Subject<void>();
  isPromotionOfferSignal = signal(false);
  constructor(
    private router: Router,
    private injector: Injector,
    private commonService: CommonService,
    private salesJourneyIdentityService: SalesJourneyIdentityService
  ) {
    this.checkApiPostReqProfileImg = false;
    this.checkApiPostReqRemovePhoto = false;
    this.preferredCurrency = "GBP";
  }

  getCustomerId() {
    this.commonService.getUid("RequestBodyService").subscribe((res) => {
      this.customerId = res;
      console.log("The customer ID is:", this.customerId);
    })
  }
  // This function is used to generate the request parameters for the resolver.
  // It takes an array of module names as input and returns an object with a
  // single key, request, which maps to an array of objects, each of which
  // contains the request parameters for a single resolver request.
  public getResolverRequests(moduleNames) {
    this.getCustomerId();
    if (this.validateRequest()) {
      let parameter = { request: [] };
      for (var i = 0; i < moduleNames?.length; ++i) {
        parameter.request.push({
          page_id: i + 1,
          screen_id: i + 1,
          module_name: moduleNames[i],
          customer_id: this.customerId,
        });
      }

      return parameter;
    }
  }


  /**
   * This function is used to sync the request for sales.
   * @param anonymousId
   * @param customerId
   */

  public syncRequestForSales(anonymousId: string, customerId: any) {
    if (this.validateRequest()) {
      const module = {
        module_name: "partial_registration_exists_user",
        anonymous_id: anonymousId,
        customer_id: customerId,
      };
      return {
        modules: [
          this.salesJourneyIdentityService.applyIdentityEnvelope(module, {
            anonymousId,
            customerId: customerId != null ? String(customerId) : null,
          }),
        ],
      };
    }
  }

  // This function is used to get the request body for get prize breakdown tickets based on date
  getOneSignalTokenRequest(oneSignalData: Data): Observable<any | null> {
    if (!oneSignalData?.onesignal_id && !oneSignalData?.subscription_id) {
      return of(null);
    }

    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      takeUntil(this.destroy$),
      map((userId) => {
        if (!userId || userId.length <= 5 || !this.validateRequest()) {
          return null;
        }
        return {
          modules: [
            {
              module_name: "update_onesignal_token_to_clevertap",
              user_id: userId,
              onesignal_id: oneSignalData?.onesignal_id,
              onesignal_token_status: oneSignalData?.token_status,
              player_id: oneSignalData?.subscription_id,
            },
          ],
        };
      })
    );
  }

  // This function is used to get the request body for save private syndicate tickets
  public getSaveLotteryTicketRequest(
    syndicateId: string,
    productArray: any[]
  ): Observable<any | null> {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      takeUntil(this.destroy$),
      map((userId) => {
        if (!userId || !this.validateRequest()) {
          return null;
        }

        return {
          modules: [
            {
              session_ID: "jsdgfkjsdhfk",
              page_ID: "4",
              screen_id: "4.9",
              action: "syndicate_buy",
              website: "Lotto Social",
              website_id: "33",
              source_site: "mobi.lottosocial.com",
              module_name: "save_private_syndicate_tickets",
              user_id: userId,
              private_syndicate_id: syndicateId,
              product_group: productArray,
              requestid: uuidv4(),
              document: "/api_post_request/save_private_syndicate_tickets",
            },
          ],
        };
      })
    );
  }


  // This function is used to get the request body for get paymil card details using offer id and order id [ we already move]
  getCardDetails(offerId, orderId?) {
    this.getCustomerId();
    const paymilDetails = {
      session_ID: "CommonService.session.session_id",
      page_ID: "4",
      screen_id: "4.10",
      action: "get_card_details",
      website: "Lotto Social",
      website_id: "33",
      source_site: "mobi.lottosocial.com",
      module_name: "get_customer_paymill_card_details",
      customer_id: this.customerId,
      p_type: "10",
    };
    if (orderId) {
      return { ...paymilDetails, ...{ private_syndicate_id: offerId } };
    }
    return { ...paymilDetails, ...{ paymill_offer_id: offerId } };
  }


  // This function is used to get the request body for activate trigger
  public activateTrigger(jackpot: any, alias: string): Observable<any | null> {
    const isPromo = this.isPromotionOfferSignal() || localStorage.getItem("syndicatePromotion") === "true" ? true : false;
    if (this.validateRequest()) {
      if (isPromo) {
        jackpot.call_from = "jackpot_trigger_promotion";
        jackpot.free_jackpot = true;
        jackpot.alternate_free_jackpot = true;
      }
      return this.commonService.getUid("RequestBodyService").pipe(
        take(1),
        takeUntil(this.destroy$),
        map((userId) => {
          if (!userId) {
            return null;
          }

          return {
            modules: [
              {
                module_name: "update_jackpot_trigger",
                user_id: userId,
                prosub_id: jackpot.offer_link_id,
                offer_id: jackpot?.offer_id,
                free_jackpot: alias?.includes("free")
                  ? true
                  : jackpot?.free_jackpot,
                alternate_free_jackpot: jackpot?.alternate_free_jackpot,
                free_offer_id: jackpot?.free_offer_id,
                rollover_jackpot_action: "activate",
                requestid: uuidv4(),
                alias: alias,
                document: "/api_post_request/activate_jackpot_trigger",
                call_from: jackpot?.call_from || "",
              },
            ],
          };
        })
      );
    }
  }

  // ✅ Build the request body for deactivating jackpot trigger
  public deactivateTrigger(jackpot: any): Observable<any | null> {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      takeUntil(this.destroy$),
      map((userId) => {
        if (!userId || !this.validateRequest()) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "update_jackpot_trigger",
              user_id: userId,
              prosub_id: jackpot.offer_link_id,
              offer_id: jackpot.offer_id,
              free_jackpot: jackpot?.free_jackpot,
              alternate_free_jackpot: jackpot?.alternate_free_jackpot,
              free_offer_id: jackpot?.free_offer_id,
              rollover_jackpot_action: "deactivate",
              requestid: uuidv4(),
              document: "/api_post_request/deactivate_jackpot_trigger",
            },
          ],
        };
      })
    );
  }


  // This function is used to get the request body for get prize breakdown tickets based on date
  getCheckedTicketsRequest(claim_event_id, check_date) {
    if (this.validateRequest()) {
      this.getCustomerId();
      return {
        modules: [
          {
            module_name: "get_prize_breakdown_ticket_based_on_date",
            customer_id: this.customerId,
            claim_event_id,
            check_date,
          },
        ],
      };
    }
  }

  // This function is used to get the request body for find module is valid or not
  getInvalidModuleNamesRequest(moduleRequests) {
    if (this.validateRequest()) {
      const parameter = { request: [] };
      parameter.request.push({
        module_name: "validate_module_name_serial",
        module_data: moduleRequests,
      });

      return parameter;
    }
  }

  // This function is used to get the request body for get private syndicate terms and conditions
  getAgreementDataRequest(syndicateId, syndicateType, syndicateRowData?: any) {
    if (this.validateRequest()) {
      return {
        modules: [
          {
            module_name: "private_syndicate_terms_condition",
            syndicate_id: syndicateId ? syndicateId : null,
            syndicate_type: syndicateType,
            syndicate_name: syndicateRowData ? syndicateRowData?.title : null,
            type: syndicateRowData ? syndicateRowData.type : null,
            product_group: syndicateRowData ? syndicateRowData.product_group : null,
          },
        ],
      };
    }
  }



  // This function is used to get the request body for get affiliate page product and affiliate page ticket number details.
  getAffiliate() {
    if (this.validateRequest()) {
      this.getCustomerId();
      return {

        request: [
          {
            session_ID: "CommonService.session.session_id",
            action: "affiliate_page",
            website: "Lotto Social",
            website_id: "33",
            source_site: "mobi.lottosocial.com",
            module_name: "get_affiliate_page_product_details",
            customer_id: this.customerId,
            product_id: "32",
            offer_id: "1136",
          },
          {
            session_ID: "CommonService.session.session_id",
            action: "affiliate_page",
            website: "Lotto Social",
            website_id: "33",
            source_site: "mobi.lottosocial.com",
            module_name: "get_affiliate_page_ticket_number_details",
            customer_id: this.customerId,
            product_id: "36",
            offer_id: "1079",
          },
        ],
      };
    }
  }


  getWPContent(contentType: string): Observable<any | null> {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      takeUntil(this.destroy$),
      map((userId) => {
        if (!userId) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "get_wp_page_contents",
              page_name: contentType,
              user_id: userId,
            },
          ],
        };
      })
    );
  }


  getWPContentHelp(id: string): Observable<any | null> {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      takeUntil(this.destroy$),
      map((userId) => {
        if (!userId) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "get_wp_page_contents",
              wp_id: id,
              user_id: userId,
            },
          ],
        };
      })
    );
  }



  // This function is used to get the request body for get raffle numbers for purchased ticket
  async getRaffleNum(syndicateId: string): Promise<any | null> {
    if (!this.validateRequest()) {
      return null;
    }

    const userId = await firstValueFrom(
      this.commonService.getUid("RequestBodyService").pipe(take(1))
    );

    if (!userId) {
      return null;
    }

    return {
      modules: [
        {
          module_name: "sync_users_purchased_ticket_raffle",
          user_id: userId,
          syndicate_id: syndicateId,
          requestid: uuidv4(),
          document: "/api_post_request/sync_users_purchased_ticket_raffle",
        },
      ],
    };
  }


  // This function is used to get the request body for validate product purchase and insert customer purchase
  public validatePurchaseProduct(purchaseDetail, document, one_click_pay = false) {
    const authService = this.injector.get(AuthService);
    const auth = getAuth();

    return from(authService.getUid("requestBodyService")).pipe( // ✅ wrap Promise
      switchMap((uid) => {
        if (!purchaseDetail || !purchaseDetail.offers || !this.validateRequest()) {
          return throwError(() => new Error('err_invalid_purchase_detail'));
        }
        // Guard against userDetails$ being undefined (e.g. on slow iOS devices before auth resolves)
        const userDetails$ = authService.userDetails$ ?? of(null);
        return userDetails$.pipe(
          take(1),
          map((user: any) => {
            const preferredCurrency = user?.preferred_currency ?? "GBP";

            const voucherDiscount = purchaseDetail?.voucher_discount || 0;
            const currencyValue = purchaseDetail?.prize || purchaseDetail?.offers?.price;

            const purchaseModule = {
                  user_id: uid,
                  anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
                  action: "insert_customer_purchase",
                  module_name: "insert_customer_purchase",
                  offer_id: purchaseDetail?.offers?.offerid ?? purchaseDetail?.offer_id,
                  prosub_id: purchaseDetail?.prosub_id ?? localStorage.getItem("prosub_id"),
                  TP1: purchaseDetail?.TP1 ?? purchaseDetail?.tp1 ?? localStorage.getItem("TP1") ?? this.getCookie("TP1") ?? "",
                  TP2: purchaseDetail?.TP2 ?? purchaseDetail?.tp2 ?? localStorage.getItem("TP2") ?? this.getCookie("TP2") ?? "",
                  TP3: purchaseDetail?.TP3 ?? purchaseDetail?.tp3 ?? localStorage.getItem("TP3") ?? this.getCookie("TP3") ?? "",
                  T_ID: purchaseDetail?.T_ID ?? purchaseDetail?.t_id ?? localStorage.getItem("T_ID") ?? this.getCookie("T_ID") ?? "",
                  Other: purchaseDetail?.other ?? localStorage.getItem("Other") ?? this.getCookie("Other") ?? "",
                  Value: purchaseDetail?.offers?.price - (purchaseDetail?.offers?.price * voucherDiscount),
                  preferred_currency: {
                    Value: purchaseDetail?.offers?.int_price?.[preferredCurrency]
                      ? purchaseDetail.offers.int_price[preferredCurrency] -
                      (purchaseDetail.offers.int_price[preferredCurrency] * voucherDiscount)
                      : currencyValue - (currencyValue * voucherDiscount),
                    currency: preferredCurrency,
                  },
                  p_type: purchaseDetail?.p_type ?? "10",
                  product_description: Array.isArray(purchaseDetail?.offers?.description)
                    ? purchaseDetail.offers.description[0]
                    : purchaseDetail?.offers?.description,
                  visitorid: 111,
                  name: purchaseDetail?.name,
                  requestid: uuidv4(),
                  document,
                  one_click_pay,
                };
            const req = {
              modules: [
                this.salesJourneyIdentityService.applyIdentityEnvelope(purchaseModule, {
                  userId: auth?.currentUser?.isAnonymous ? null : uid,
                  anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
                  // Omit customerId: resolve the actual SQL ID when available.
                  msn: user?.msn,
                }),
              ],
            };

            if (purchaseDetail.page === "voucher") {
              req.modules[0]["voucher_code_id"] = purchaseDetail.voucher_code_id;
            }
            if (purchaseDetail.addons?.length > 0) {
              req.modules[0]["add_on"] = purchaseDetail.addons;
            }

            return req;
          })
        );
      })
    );
  }


  public validatePurchaseProductJSON(purchaseDetail, document) {
    const authService = this.injector.get(AuthService);
    const auth = getAuth();
    if (!auth?.currentUser?.isAnonymous) {
      authService.userDetails$.pipe(take(1)).subscribe((user) => {
        this.preferredCurrency = user?.preferred_currency ? user?.preferred_currency : "GBP";
      });
    } else {
      this.preferredCurrency = "GBP";
    }
    if (!purchaseDetail) return;
    if (this.validateRequest()) {
      const prosubId = purchaseDetail.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id");

      const truevoId = "22";
      const truevoDetails = authService?.truevoDetails$?.value;
      if (truevoDetails && truevoDetails?.prosubs?.includes(prosubId)) {
        purchaseDetail.p_type = truevoId;
      }

      this.getCustomerId();
      const purchaseModule = {
            user_id: this.customerId, // <-- from service now
            anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
            action: "insert_customer_purchase",
            offer_id:
              purchaseDetail && purchaseDetail.offers && purchaseDetail.offers.offerid
                ? purchaseDetail.offers.offerid
                : purchaseDetail?.offer_id,
            prosub_id: purchaseDetail.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id"),
            TP1: purchaseDetail.TP1
              ? purchaseDetail.TP1
              : purchaseDetail.tp1
                ? purchaseDetail.tp1
                : localStorage.getItem("TP1")
                  ? localStorage.getItem("TP1")
                  : this.getCookie("TP1")
                    ? this.getCookie("TP1")
                    : "",
            TP2: purchaseDetail.TP2
              ? purchaseDetail.TP2
              : purchaseDetail.tp2
                ? purchaseDetail.tp2
                : localStorage.getItem("TP2")
                  ? localStorage.getItem("TP2")
                  : this.getCookie("TP2")
                    ? this.getCookie("TP2")
                    : "",
            TP3: purchaseDetail.TP3
              ? purchaseDetail.TP3
              : purchaseDetail.tp3
                ? purchaseDetail.tp3
                : localStorage.getItem("TP3")
                  ? localStorage.getItem("TP3")
                  : this.getCookie("TP3")
                    ? this.getCookie("TP3")
                    : "",
            T_ID: purchaseDetail.T_ID
              ? purchaseDetail.T_ID
              : purchaseDetail.t_id
                ? purchaseDetail.t_id
                : localStorage.getItem("T_ID")
                  ? localStorage.getItem("T_ID")
                  : this.getCookie("T_ID")
                    ? this.getCookie("T_ID")
                    : "",
            Other: purchaseDetail.other
              ? purchaseDetail.other
              : localStorage.getItem("Other")
                ? localStorage.getItem("Other")
                : this.getCookie("Other")
                  ? this.getCookie("Other")
                  : "",
            Value: purchaseDetail.offers?.price,
            preferred_currency: {
              Value: purchaseDetail.offers?.int_price?.[this.preferredCurrency]
                ? purchaseDetail.offers?.int_price?.[this.preferredCurrency]
                : purchaseDetail?.prize,
              currency: this.preferredCurrency,
            },
            p_type: purchaseDetail?.p_type ? purchaseDetail?.p_type : "10",
            product_description:
              purchaseDetail && purchaseDetail?.offers && purchaseDetail?.offers.description
                ? purchaseDetail.offers.description
                : "",
            visitorid: 111,
            name: purchaseDetail?.name,
            requestid: uuidv4(),
            document: document,
            PWA_ST_syndicate_request_id: purchaseDetail?.ST_syndicate_request_id
              ? purchaseDetail?.ST_syndicate_request_id
              : "",
          };
      const req = {
        modules: [
          this.salesJourneyIdentityService.applyIdentityEnvelope(purchaseModule, {
            userId: auth?.currentUser?.isAnonymous ? null : this.customerId,
            // Omit customerId: resolve the actual SQL ID when available (this.customerId is Firebase UID).
            anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
          }),
        ],
      };
      if (purchaseDetail.page === "voucher") {
        req.modules[0]["voucher_code_id"] = purchaseDetail.voucher_code_id;
      }
      return req;
    }
  }


  // This function is used to get the request body for validate purchase and insert customer purchase for topup
  public validatePurchaseProductForTopUp(purchaseDetail: any, document: any) {
    const authService = this.injector.get(AuthService);

    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      switchMap((userId) => {
        return authService.userDetails$.pipe(
          take(1),
          map((user: any) => {
            this.preferredCurrency = user?.preferred_currency ? user?.preferred_currency : "GBP";

            if (!this.validateRequest()) {
              return null;
            }

            return {
              modules: [
                {
                  action: "insert_customer_purchase",
                  module_name: "insert_customer_purchase",
                  user_id: userId,   // ✅ from commonService, not localStorage
                  offer_id: purchaseDetail?.offer_id,
                  prosub_id: "",
                  value: purchaseDetail.prize,
                  p_type: purchaseDetail.p_type ? purchaseDetail.p_type : "10",
                  product_description: "Award",
                  requestid: uuidv4(),
                  document: document,
                  preferred_currency: {
                    Value: purchaseDetail.offers?.int_price?.[this.preferredCurrency],
                    currency: this.preferredCurrency,
                  },
                },
              ],
            };
          })
        );
      })
    );
  }


  // This function is used to get the request body for validate purchase and insert customer purchase for private syndicate
  public validatePurchaseProductForCreatingPS(purchaseDetail: any, document: any) {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      map((userId) => {
        if (!this.validateRequest()) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "create_order_id_for_PS_Payment",
              user_id: userId,   // ✅ from commonService, not localStorage
              param: {
                ps_cart: purchaseDetail.cart,
                prosub_id: "",
              },
              requestid: uuidv4(),
              document: document,
              ls_sandbox: true
            },
          ],
        };
      })
    );
  }


  // This function is used to get the request body for process payment for existing card
  public validateProcessPaymentRequest(customerPurchaseId: string, card: any, document: any): Observable<any> {

    if (!customerPurchaseId || !card?.encrypt_card_id) {
      return of(null);
    }

    return this.commonService.getUid("").pipe(map((res) => {
      if (this.validateRequest()) {
        return {
          modules: [
            {
              user_id: res,
              customer_purchase_id: customerPurchaseId,
              module_name: "process_payment_existing_card",
              customer_payment_stored_detail_id: card.encrypt_card_id,
              ip: CommonVariable.userIP,
              requestid: uuidv4(),
              document: document,
            },
          ],
        };
      }
      // Explicitly return null when validation fails so downstream switchMap never receives undefined
      return null;
    }));
  }

  // This function is used to get the request body for credit topup

public validateProcessPaymentRequestForTopUp(paymentDetails: any) {
  this.getUserPreferredCurrency();

  return this.commonService.getUid('RequestBodyService').pipe(
    take(1),
    map((userId) => {

      if (!this.validateRequest()) {
        return null;
      }

      const preferredPrice =
        paymentDetails?.offers?.int_price?.[this.preferredCurrency] ||
        paymentDetails?.offers?.int_price?.['GBP'];

      const payload = {
        modules: [
          {
            module_name: 'credit_topup',
            user_id: userId,
            param: {
              award_id: paymentDetails?.offer_id,
              price: paymentDetails?.prize,
              customer_payment_stored_detail_id:
                paymentDetails?.card?.customer_Payment_stored_detail_ID,
              ip: CommonVariable.userIP,
              preferred_currency: {
                price: preferredPrice,
                currency: this.preferredCurrency || 'GBP',
              },
              ls_sandbox: paymentDetails?.isSandbox || false,
            },
            requestid: uuidv4(),
            document: '/api_post_request/credit_topup',
          },
        ],
      };

      return payload;
    })
  );
}

  public confirmGoldRingSelection(reqObj: any) {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      map((userId) => {
        if (!this.validateRequest()) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "gold_ring_request",
              user_id: userId, // ✅ from commonService
              requestid: uuidv4(),
              document: "/api_post_request/gold_ring_request",
              ...reqObj,
            },
          ],
        };
      })
    );
  }

  public getPSCreatingCartRequest(purchaseDetail: any) {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      map((userId) => {
        if (!this.validateRequest()) {
          return null;
        }

        return {
          modules: [
            {
              module_name: "create_order_id_for_PS_Payment",
              user_id: userId, // ✅ using commonService
              param: {
                ps_cart: purchaseDetail.cart,
                prosub_id: "",
              },
              requestid: uuidv4(),
              document: "/api_post_request/create_order_id_for_PS_Payment",
            },
          ],
        };
      })
    );
  }


  // This function is used to get the request body for charity vote functionality
  public voteCharity(award_id: string, points: number) {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      map((userId) => {
        if (this.validateRequest()) {
          return {
            modules: [
              {
                user_id: userId, // ✅ now using commonService
                module_name: "charity_vote",
                param: {
                  award_id: award_id,
                  charity_points: points,
                },
                requestid: uuidv4(),
                document: "/api_post_request/charity_vote",
              },
            ],
          };
        }
      })
    );
  }


  // This function is used to get the request body for to validate request is validate or not
  private validateRequest(): boolean {
    return true;
  }

  public validateApiPostReq(document: string) {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      filter((userId): userId is string => !!userId), // ✅ ensures userId is not null
      map((userId) => ({
        modules: [
          {
            document,
            module_name: "only_validate_api_post_req",
            requestid: uuidv4(),
            user_id: userId,
          },
        ],
      }))
    );
  }


  public twilioRequest(msn, ip) {
    if (this.validateRequest()) {
      return {
        module_name: "msn_validation_twilio",
        param: {
          mobile_number: msn,
          ip_address: ip,
        },
      };
    }
  }

  setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  // This function used to get cookie value to the given name.
  getCookie(name) {
    try {
      var nameEQ = name + "=";
      var cookie = document.cookie?.split(";");
      for (var i = 0; i < cookie?.length; i++) {
        var c = cookie[i];
        while (c.charAt(0) == " ") c = c?.substring(1, c?.length);
        if (c?.indexOf(nameEQ) == 0) return c?.substring(nameEQ?.length, c?.length);
      }
      return null;
    } catch (e) { }
  }

  // This function is used to get the request body for auto credit refresh
  public refreshToken(creditAmount: any, asyncCallDocumentId = '') {
    const isPromo = this.isPromotionOfferSignal();
    if (isPromo) {
      creditAmount.call_from = "auto_topup_promotion";
    }
    if (!this.validateRequest()) {
      return of(null); // or throwError, depending on your pattern
    }

    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      filter((userId): userId is string => !!userId), // ✅ skip if null
      map((userId) => ({
        modules: [
          {
            module_name: "auto_credit_refresh",
            user_id: userId, // ✅ now from commonService
            status: 1,
            low_trigger_threshold: creditAmount.low_trigger_threshold,
            refresh_amount: creditAmount.credit_refresh_output,
            requestid: uuidv4(),
            document: "/api_post_request/auto_credit_refresh",
            lambda_async_fb_path: "/async_module_call/" + asyncCallDocumentId,
            call_from: creditAmount?.call_from || "",
          },
        ],
      }))
    );
  }


  // This function is used to get the request body for credit refresh (OFF mode)
  public refreshTokenOff(creditAmount: any, asyncCallDocumentId = '') {
    console.log("[refreshTokenOff] 🔹 Input Params:", {
      creditAmount,
      asyncCallDocumentId,
    });

    if (!this.validateRequest()) {
      console.warn("[refreshTokenOff] ❌ Request validation failed");
      return of(null); // or throwError, depending on how you handle invalid requests
    }

    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      map((userId) => {
        if (!userId) {
          console.error("[refreshTokenOff] ❌ userId is null/undefined");
          return null;
        }

        const requestBody = {
          modules: [
            {
              module_name: "auto_credit_refresh",
              user_id: userId, // ✅ now from commonService
              status: 0, // OFF
              low_trigger_threshold: creditAmount.low_trigger_threshold,
              refresh_amount: creditAmount.credit_refresh_output,
              requestid: uuidv4(),
              document: "/api_post_request/auto_credit_refresh",
              lambda_async_fb_path: "/async_module_call/" + asyncCallDocumentId,
            },
          ],
        };

        console.log("[refreshTokenOff] ✅ Final Request Body:", requestBody);
        return requestBody;
      })
    );
  }


  public updatePrefrenceSetting(smsPref, emailPref, userId) {
    if (this.validateRequest()) {
      const authService = this.injector?.get(AuthService);
      return {
        modules: [
          {
            module_name: "update_customer_promotional_prefs",
            user_id: userId,
            sms_pref: smsPref,
            email_pref: emailPref,
            push_pref: authService?.push_pref?.() ?? true,
            website_id: "33",
          },
        ],
      };
    }
  }

  public insertOnesignalSubscriptionId(userId: string, deviceId: string, subscriptionId: string) {
    const authService = this.injector?.get(AuthService);
    console.warn("insertOnesignalSubscriptionId");
    const data = {
      module_name: "customer_push_notification_tokens",
      user_id: userId,
      device_name: CommonVariable.device.operatingSystem,
      onesignal_id: deviceId, // we are using unique device id as a onesignal_id
      subscription_id: subscriptionId,
      subscription_status: true,
      fingerprint_id: deviceId,
      browser: authService?.getBrowserVersion() || "N/A",
    };
    if (this.validateRequest()) {
      return {
        modules: [data],
      };
    }
  }

  public pushNotificationRequest(userId: string) {
    if (this.validateRequest()) {
      return {
        modules: [
          {
            module_name: "customer_push_notification_prize",
            user_id: userId,
          },
        ],
      };
    }
  }
  // This method has been use to get user preferred currency [ we already move]
  getUserPreferredCurrency() {
    const authService = this.injector.get(AuthService);
    authService.userDetails$.pipe(takeUntil(this.destroy$)).subscribe((user: Data) => {
      try {
        this.preferredCurrency = user?.preferred_currency ?? "GBP";
      } catch (error) {
        throw new Error(`Preferred Currency Not found ${error}`);
      }
    });
  }
  // The ngOnDestroy() method, we emit a value and complete the destroy$ subject, which unsubscribes from the observable.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // This function is used to get the request body for credit refresh
  public getCreditRefreshRequest() {
    return this.commonService.getUid("RequestBodyService").pipe(
      take(1),
      filter((userId): userId is string => !!userId), // ✅ skip null userId
      map((userId) => ({
        modules: [
          {
            module_name: "module_auto_refresh_credit_charge",
            user_id: userId, // ✅ now from commonService
          },
        ],
      }))
    );
  }

  // This function is used to get the request body for get data for chatlio
  getDataforChatlio(userId) {
    if (this.validateRequest()) {
      return {
        modules: [
          {
            module_name: "get_data_for_chatbot_llm",
            user_id: userId,
            calling_method: "db",
          },
        ],
      };
    }
  }
}
